'use client';

import { useFocus } from '@react-aria/interactions';
import { AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/navigation';
import type { ChangeEvent, FormEvent} from 'react';
import { useState } from 'react';

import { Close, Search } from '@zoomit/icons';
import { Box, cn, useDebounce, useHotkeys } from '@zoomit/primitives';
import { onSearchTerm, useWebKitCustomization } from '@zoomit/web-kit';
import { Input } from '@zoomit/zoomit-ds';

import type { MenuParserData } from '../../../types/menus';
import { HeightMotion } from '../../shared/HeightMotion';
import { SearchInstance } from '../SearchInstance';

type Props = {
  className?: string;
  searchInstanceRenderer?: MenuParserData['searchInstanceRenderer'];
};

export const SearchSection = (props: Props) => {
  const { searchInstanceRenderer, className } = props;
  const router = useRouter();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { focusProps } = useFocus({
    onFocus: () => setIsSearchOpen(true),
  });

  const redirectToSearch = (e: FormEvent<HTMLFormElement>) => {
    const form = new FormData(e.currentTarget);
    e.preventDefault();
    const query = form.get('search');
    setIsSearchOpen(false);
    router?.push(`/search/${query}`);
  };

  useHotkeys([['Escape', () => setIsSearchOpen(false)]]);
  const searchSettings = useWebKitCustomization((settings) => settings.search);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    if (event.target.value.length !== 0) onSearchTerm(event.target.value);
  };

  return (
    <>
      <form onSubmit={redirectToSearch} className={className}>
        <Input
          className={'!py-4 transition duration-300 ease-in-out'}
          name="search"
          variant="rounded"
          rightNode={
            <Box
              className={cn(
                'grid h-6 w-6 place-content-center rounded-full transition duration-200',
                searchQuery ? 'bg-grey3' : 'bg-primary1',
              )}
            >
              {searchQuery ? (
                <Close
                  className={'fill-black5 !h-5 !w-5 cursor-pointer'}
                  onClick={() => setSearchQuery('')}
                />
              ) : (
                <Search className={'fill-white'} />
              )}
            </Box>
          }
          value={searchQuery}
          onChange={handleSearch}
          placeholder={searchSettings?.placeholder || 'جستجو ...'}
          {...focusProps}
        />
      </form>
      {isSearchOpen && (
        <Box
          onClick={() => setIsSearchOpen(false)}
          className={'fixed inset-0 top-0 left-0 z-40 h-screen w-screen'}
        />
      )}
      <AnimatePresence>
        {isSearchOpen && (
          <Box
            className={
              'bg-card shadow-elevation absolute top-16 left-0 z-50 w-screen overflow-hidden shadow-[0_10px_10px]'
            }
          >
            <HeightMotion initialAnimation>
              <Box className={cn('mx-auto w-[648px] overflow-hidden', debouncedSearchQuery && 'pb-4')}>
                {searchInstanceRenderer ? (
                  searchInstanceRenderer({
                    searchQuery: debouncedSearchQuery,
                    onClose: () => setIsSearchOpen(false),
                  })
                ) : (
                  <SearchInstance searchQuery={debouncedSearchQuery} onClose={() => setIsSearchOpen(false)} />
                )}
              </Box>
            </HeightMotion>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
};
